/** Position ownership and deterministic colleague policies, independent of presentation and AI providers. */
import { alongTrackM, bearingDeg, crossTrackM, destination, distanceM, NM_TO_M, toMagneticDeg, type AirportData, type ControllerPosition } from '@atc/core';
import { isRemovedPhase, type Aircraft } from './aircraft';
import type { Instruction } from './commands';
import type { CommandResult } from './sim-contracts';
import { occupiedEnd, runwaysIntersect, shortFinalEnd } from './runways';

export interface ControlRecord {
  coaching?: boolean;
  owner: ControllerPosition;
  frequency: ControllerPosition;
  offeredTo?: ControllerPosition;
  /** When the current offer was made, so a late transfer can tell a fresh offer from an ignored one. */
  offeredAtS?: number;
  acceptedAtS?: number;
  transferredAtS?: number;
  transferTo?: ControllerPosition;
  transferAtS?: number;
}
export interface CoordinationMessage { timeS: number; from: ControllerPosition; text: string }
export type CoordinationRequest =
  | { kind: 'accept'; callsign: string }
  | { kind: 'transfer'; callsign: string; to: ControllerPosition }
  | { kind: 'arrivalFlow'; mode: 'normal' | 'spaced' | 'suspended' }
  | { kind: 'crossRunway'; callsign: string }
  | { kind: 'holdPosition'; callsign: string };
export interface OperationsSnapshot {
  position: 'tower' | 'legacy';
  arrivalFlow: 'normal' | 'spaced' | 'suspended';
  messages: CoordinationMessage[];
  aircraft: { callsign: string; control: ControlRecord; thresholdInS?: number; crossing?: string }[];
}
export interface OperationsHost {
  airport: AirportData;
  active(): Aircraft[];
  now(): number;
  execute(ac: Aircraft, instructions: Instruction[]): CommandResult;
  checkin(ac: Aircraft): void;
  requestTransfer(ac: Aircraft, to: ControllerPosition): number;
  transfer(ac: Aircraft, to: ControllerPosition): void;
}

/** ASSUMPTION: about three miles at approach speed, before the landing request at three miles. */
const LATE_TRANSFER_S = 90;
/** An offer younger than this is still fresh, even this close in: the player has not had a chance to take it. */
const IGNORED_OFFER_S = 30;
const vectorHeading = (degrees: number) => Math.round((((degrees % 360) + 360) % 360) / 5) * 5 % 360 || 360;

export function createOperations(host: OperationsHost, enabled: boolean, coaching = true) {
  const messages: CoordinationMessage[] = [];
  let arrivalFlow: OperationsSnapshot['arrivalFlow'] = 'normal';
  const recovery = new Map<number, { lat: number; lon: number; stage: number }>();
  const say = (from: ControllerPosition, text: string) => {
    messages.push({ timeS: host.now(), from, text });
    if (messages.length > 80) messages.shift();
  };
  const eta = (ac: Aircraft) => {
    const rw = ac.approach?.runway ?? ac.arrivalRunway;
    if (!rw || ac.onGround || recovery.has(ac.id)) return undefined;
    const nm = -alongTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon) / NM_TO_M;
    return nm > 0 ? Math.round(nm / Math.max(80, ac.groundspeedKt) * 3600) : undefined;
  };
  const offer = (ac: Aircraft, to: ControllerPosition) => {
    if (!ac.control || ac.control.offeredTo === to || ac.control.transferTo) return;
    ac.control.offeredTo = to;
    ac.control.offeredAtS = host.now();
    say(ac.control.owner, `${ac.callsign}, transfer offered to ${to}${ac.approach ? `, established runway ${ac.approach.runway.name}` : ', ready'}.`);
  };
  return {
    checkpoint() { return structuredClone({ messages, arrivalFlow, recovery }); },
    restore(state: { messages: CoordinationMessage[]; arrivalFlow: OperationsSnapshot['arrivalFlow']; recovery: Map<number, { lat: number; lon: number; stage: number }> }) {
      messages.splice(0, messages.length, ...state.messages); arrivalFlow = state.arrivalFlow;
      recovery.clear(); for (const [id, leg] of state.recovery) recovery.set(id, leg);
    },
    initialize(ac: Aircraft) {
      if (!enabled) return;
      const owner: ControllerPosition = ac.kind === 'arrival' ? 'approach' : ac.kind === 'departure' ? 'ground' : 'departure';
      ac.control = { owner, frequency: owner, coaching };
      if (ac.kind === 'departure' && ac.departureRunway && ac.holdShortPos) {
        const rw = ac.departureRunway;
        // Preserve the queue's 80 m spacing when shifting it away from the runway.
        const p = destination(rw.lat, rw.lon, rw.headingDeg + 270, Math.max(110, distanceM(rw.lat, rw.lon, ac.holdShortPos.lat, ac.holdShortPos.lon) + 50));
        ac.holdShortPos = { ...p, headingDeg: ac.holdShortPos.headingDeg };
        if (ac.phase === 'holdShort') { ac.lat = p.lat; ac.lon = p.lon; }
        if (ac.taxiRoute?.length) ac.taxiRoute[ac.taxiRoute.length - 1] = p;
      }
      if (ac.kind === 'arrival' && !ac.approach && ac.arrivalRunway) host.execute(ac, [{ kind: 'approach', runway: ac.arrivalRunway.name }]);
      if (ac.kind === 'departure') host.execute(ac, [{ kind: 'altitude', altitudeFt: Math.max(1000, Math.round(host.airport.initialAltitudeFt / 1000) * 1000), expedite: false }]);
      if (ac.kind === 'departure' && ac.phase === 'holdShort') offer(ac, 'tower');
    },
    prepareArrival(ac: Aircraft) {
      if (!enabled || ac.kind !== 'arrival' || ac.approach) return;
      const rw = ac.arrivalRunway;
      if (!rw) return;
      // Spawn placement only: never move an aircraft already flying in the simulation.
      const ahead = host.active().filter((a) => a !== ac && a.arrivalRunway?.name === rw.name && !a.onGround && !isRemovedPhase(a.phase));
      const furthest = Math.max(0, ...ahead.map((a) => -alongTrackM(rw.lat, rw.lon, rw.headingDeg, a.lat, a.lon) / NM_TO_M));
      const latestEtaS = Math.max(0, ...ahead.map((a) => Math.max(0, -alongTrackM(rw.lat, rw.lon, rw.headingDeg, a.lat, a.lon) / NM_TO_M) / Math.max(100, a.groundspeedKt) * 3600));
      const targetHeadwayS = arrivalFlow === 'spaced' ? 150 : 100;
      const nm = Math.max(12, furthest + 6, (latestEtaS + targetHeadwayS) * 180 / 3600);
      const p = destination(rw.lat, rw.lon, rw.headingDeg + 180, nm * NM_TO_M);
      ac.lat = p.lat; ac.lon = p.lon; ac.headingDeg = ac.trackDeg = rw.headingDeg;
      // Placed on the final, already vectored there: it no longer flies its STAR, so the approach
      // clearance below is taken from this heading, not "via" the STAR's next fix.
      ac.nav = { mode: 'heading', headingDeg: rw.headingDeg, routeIdx: 0 };
      ac.altitudeFt = ac.targetAltitudeFt = Math.round((rw.elevationM / 0.3048 + nm * 300) / 100) * 100;
      ac.iasKt = ac.groundspeedKt = 180;
      host.execute(ac, [{ kind: 'approach', runway: rw.name }]);
    },
    canTransmit(ac: Aircraft): string | null {
      if (ac.control?.transferTo) return `Frequency transfer to ${ac.control.transferTo} is in progress.`;
      return enabled && (ac.control?.owner !== 'tower' || ac.control.frequency !== 'tower') ? `${ac.callsign} is with ${ac.control?.frequency ?? 'another controller'}. Accept the transfer before transmitting.` : null;
    },
    instructionReason(ac: Aircraft, instructions: Instruction[]): string | null {
      if (!enabled) return null;
      if (instructions.length > 1 && instructions.some((i) => i.kind === 'contact')) return 'Transfer frequency in a separate transmission after the runway instruction.';
      if (instructions.some((i) => !['land', 'lineUp', 'takeoff', 'abort', 'contact'].includes(i.kind))) return 'Tower controls runway operations. Coordinate routing and spacing with approach.';
      if (instructions.some((i) => i.kind === 'land') && !ac.approach?.established) return 'Arrival is not established; coordinate with approach.';
      return null;
    },
    goAround(ac: Aircraft) {
      if (!enabled || !ac.control) return;
      ac.control.offeredTo = 'approach';
      say('approach', `${ac.callsign}, missed approach accepted. Transfer when ready; continue the briefed runway heading and climb.`);
    },
    step() {
      if (!enabled) return;
      for (const id of recovery.keys()) if (!host.active().some((a) => a.id === id && !isRemovedPhase(a.phase))) recovery.delete(id);
      for (const ac of host.active()) {
        const c = ac.control;
        if (!c || ['exited', 'cancelled', 'forcedLanding'].includes(ac.phase)) continue;
        if (c.transferTo && host.now() >= (c.transferAtS ?? Infinity)) {
          const to = c.transferTo;
          c.owner = c.frequency = to; c.transferTo = undefined; c.transferAtS = undefined; c.transferredAtS = host.now();
          ac.pilot.request = undefined;
          if (to === 'ground') ac.removeAtS = host.now() + 30;
          if (to === 'departure' && ac.requested && host.airport.fixes.has(ac.requested)) host.execute(ac, [{ kind: 'direct', fix: ac.requested }]);
          if (to === 'approach' && ac.arrivalRunway) {
            const rw = ac.arrivalRunway;
            const final = destination(rw.lat, rw.lon, rw.headingDeg + 180, 13 * NM_TO_M);
            const base = destination(final.lat, final.lon, rw.headingDeg + 90, 3 * NM_TO_M);
            recovery.set(ac.id, { ...base, stage: 0 });
            host.execute(ac, [{ kind: 'heading', headingDeg: vectorHeading(toMagneticDeg(host.airport, bearingDeg(ac.lat, ac.lon, base.lat, base.lon))) }, { kind: 'altitude', altitudeFt: Math.ceil((host.airport.elevationFt + 3000) / 1000) * 1000, expedite: false }, { kind: 'speed', speedKt: 180 }]);
          }
          host.transfer(ac, to); say(to, `${ac.callsign}, we have control.`);
        }
        if (c.owner === 'ground' && ac.phase === 'holdShort') offer(ac, 'tower');
        if (c.owner === 'approach' && ac.approach?.established && !recovery.has(ac.id) && (eta(ac) ?? Infinity) < 210) offer(ac, 'tower');
        // An offer left waiting until short final: approach sends the crew over anyway, as a real
        // approach controller would, so the landing request and any go-around are heard on the
        // tower frequency instead of happening silently on somebody else's.
        if (c.owner === 'approach' && c.offeredTo === 'tower' && host.now() - (c.offeredAtS ?? host.now()) >= IGNORED_OFFER_S && ac.approach?.established && !ac.approach.cleared && (eta(ac) ?? Infinity) < LATE_TRANSFER_S) {
          c.acceptedAtS = c.transferredAtS = host.now(); c.owner = c.frequency = 'tower'; c.offeredTo = undefined;
          ac.pilot.request = undefined;
          host.checkin(ac);
          say('approach', `${ac.callsign} is on short final and was never accepted; sent to your frequency. Accept arrivals earlier.`);
        }
        if (c.owner === 'tower' && ac.kind === 'departure' && !ac.onGround && ac.altitudeFt >= host.airport.elevationFt + 1000 && !ac.returning) offer(ac, 'departure');
        if (c.owner === 'tower' && ac.phase === 'landed') { ac.removeAtS = undefined; offer(ac, 'ground'); }
        const leg = recovery.get(ac.id);
        if (c.owner === 'approach' && leg && host.now() % 5 === 0) {
          host.execute(ac, [{ kind: 'heading', headingDeg: vectorHeading(toMagneticDeg(host.airport, bearingDeg(ac.lat, ac.lon, leg.lat, leg.lon))) }]);
        }
        if (c.owner === 'approach' && leg && distanceM(ac.lat, ac.lon, leg.lat, leg.lon) < NM_TO_M) {
          const rw = ac.arrivalRunway!;
          const side = crossTrackM(rw.lat, rw.lon, rw.headingDeg, ac.lat, ac.lon) >= 0 ? -30 : 30;
          host.execute(ac, [{ kind: 'heading', headingDeg: vectorHeading(toMagneticDeg(host.airport, rw.headingDeg + side)) }, { kind: 'approach', runway: rw.name }]);
          recovery.delete(ac.id);
        }
      }
    },
    coordinate(request: CoordinationRequest): CommandResult {
      const fail = (message: string): CommandResult => ({ ok: false, message });
      if (!enabled) return fail('Position coordination is available in tower sessions.');
      if (request.kind === 'arrivalFlow') {
        if (!['normal', 'spaced', 'suspended'].includes(request.mode)) return fail('Unknown arrival flow.');
        arrivalFlow = request.mode;
        say('approach', `${request.mode === 'suspended' ? 'Holding new arrivals upstream' : request.mode === 'spaced' ? 'Increased spacing for new arrivals' : 'Normal arrival flow resumed'}. Traffic already inbound remains committed.`);
        return { ok: true, message: messages.at(-1)!.text };
      }
      const ac = host.active().find((a) => a.callsign === request.callsign);
      const c = ac?.control;
      if (!ac || !c) return fail('Aircraft not found.');
      if (request.kind === 'crossRunway') {
        if (c.owner !== 'ground' || !ac.crossing || ac.crossing.approved) return fail('No runway crossing is waiting for approval.');
        const rw = host.airport.runways.flatMap((r) => r.ends).find((e) => e.name === ac.crossing!.runway)!;
        const blocked = host.active().some((a) => {
          if (a === ac) return false;
          const end = occupiedEnd(a, host.airport) ?? shortFinalEnd(a);
          return end && runwaysIntersect(host.airport, rw, end);
        });
        if (blocked) return fail(`Runway ${rw.name} is in use. Hold the crossing.`);
        ac.crossing.approved = true;
        say('tower', `${ac.callsign}, runway ${rw.name} crossing approved for ground.`);
        return { ok: true, message: messages.at(-1)!.text };
      }
      if (request.kind === 'holdPosition') {
        if (c.owner !== 'tower' || !['holdShort', 'lineUp', 'linedUp'].includes(ac.phase)) return fail('Hold position applies before the takeoff roll.');
        ac.rollAfterLineUp = false;
        ac.holdPosition = true;
        ac.pilot.pending = [];
        say('tower', `${ac.callsign}, hold position. Takeoff clearance cancelled.`);
        return { ok: true, message: messages.at(-1)!.text };
      }
      if (request.kind === 'accept') {
        if (c.offeredTo !== 'tower') return fail('No inbound transfer is available.');
        c.acceptedAtS = host.now(); c.owner = c.frequency = 'tower'; c.offeredTo = undefined;
        c.transferredAtS = host.now(); ac.pilot.request = undefined;
        host.checkin(ac); say('tower', `${ac.callsign}, transfer accepted.`);
        return { ok: true, message: messages.at(-1)!.text };
      }
      if (request.kind !== 'transfer' || c.owner !== 'tower' || c.offeredTo !== request.to) return fail('Receiving controller has not accepted this transfer.');
      if (ac.conflict || ac.pilot.pending.length) return fail('Resolve the conflict and finish the pending clearance before transferring.');
      if (request.to === 'ground' && ac.phase !== 'landed') return fail('Aircraft must be clear of the runway.');
      c.acceptedAtS = host.now(); c.transferTo = request.to; c.offeredTo = undefined;
      c.transferAtS = host.requestTransfer(ac, request.to);
      return { ok: true, message: `${ac.callsign}: contact ${request.to}; awaiting readback.` };
    },
    snapshot(): OperationsSnapshot {
      return { position: enabled ? 'tower' : 'legacy', arrivalFlow, messages: messages.map((m) => ({ ...m })), aircraft: host.active().filter((a) => a.control && !['exited', 'cancelled', 'forcedLanding'].includes(a.phase)).map((a) => ({ callsign: a.callsign, control: { ...a.control! }, thresholdInS: eta(a), crossing: a.crossing && !a.crossing.approved ? a.crossing.runway : undefined })) };
    },
    get arrivalFactor() {
      if (!enabled) return 1;
      const full = host.active().some((a) => a.approach && !a.onGround && !isRemovedPhase(a.phase) && -alongTrackM(a.approach.runway.lat, a.approach.runway.lon, a.approach.runway.headingDeg, a.lat, a.lon) > 24 * NM_TO_M);
      return full || arrivalFlow === 'suspended' ? 0 : arrivalFlow === 'spaced' ? 0.65 : 1;
    },
  };
}
